import { GLYPHS } from "@utils/glyphs"
import { cn, tv } from "@utils/tw"
import { ComponentProps, useId } from "react"

type Props = ComponentProps<"div"> & {
  type: "left" | "right" | "external"
}

/**
 * Includes an arrow to the right of the content, which is only visible when a parent 'A' element is being hovered.
 *
 * Usage:
 * ```
 *   <a>
 *     <ArrowLink type="right">Click here</ArrowLink>
 *   </a>
 * ```
 */
export function ArrowLink({ type, ...props }: Props) {
  const id = useId()

  const styles = arrowLinkStyles({
    type,
  })

  return (
    <div {...props} className={cn(styles.base(), props.className)}>
      <div className={styles.label()}>{props.children}</div>
      <div className={styles.glyph()}>{GLYPHS[type]}</div>
    </div>
  )
}

const arrowLinkStyles = tv({
  base: "flex",
  slots: {
    label: "",
    glyph: [
      "pointer-events-none",
      "w-0 text-end opacity-0 whitespace-nowrap transition-all [a:hover_&]:w-[1em] [a:hover_&]:opacity-100",
      "[a:hover_&]:translate-x-0 [a:hover_&]:translate-y-0",
    ],
  },
  variants: {
    type: {
      external: { glyph: "-translate-x-full translate-y-full" },
      right: { glyph: "-translate-x-full" },
      left: { base: "flex-row-reverse", glyph: "translate-x-full text-start" },
    },
  },
})
